import React, { useEffect, useMemo } from "react";
import { Button, Empty, Space, Table, Typography, message } from "antd";
import { connect, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { getOffers, getDivisionId } from "./modules/EarlyBirdOfferList";
import { loadPage } from "./loadPage";
import { EarlyBirdOfferItemDto } from "app/shared/model/EarlyBirdOfferDto";
import {
    PublishedEventType,
    StatusIcon,
    useSignalREvent,
} from "app/helpers/signalRHelper";

interface StateProps {
    offers: EarlyBirdOfferItemDto[];
    divisionId: number;
}

interface DispatchProps {}

function formatDate(value?: string): string {
    if (!value) return "—";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("DD-MMM-YYYY") : "—";
}

function getNextAvailableYear(offers: EarlyBirdOfferItemDto[]): number {
    const currentYear = new Date().getFullYear();
    const years = (offers ?? [])
        .map((x) => Number(x.year))
        .filter((y) => Number.isFinite(y));

    if (years.includes(currentYear)) {
        return currentYear;
    }

    const nextHigher = years
        .filter((y) => y > currentYear)
        .sort((a, b) => a - b)[0];

    return Number.isFinite(nextHigher) ? nextHigher : currentYear + 1;
}

export const PageComponent: React.FC<StateProps & DispatchProps> = ({
                                                                        offers,
                                                                        divisionId,
                                                                    }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const [unpublishedChanges, setUnpublishedChanges] = React.useState(false);

    const refresh = React.useCallback(async () => {
        try {
            await loadPage(dispatch, {});
        } catch (err: any) {
            message.error(err?.message ?? "Failed to load Early Bird offers.");
        }
    }, [dispatch]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        const onDivisionChanged = () => {
            void refresh();
        };
        window.addEventListener('selectedDivisionChanged', onDivisionChanged);
        return () =>
            window.removeEventListener('selectedDivisionChanged', onDivisionChanged);
    }, [refresh]);

    useEffect(() => {
        const state = location.state as { unpublishedChanges?: boolean } | null;
        if (state?.unpublishedChanges) {
            setUnpublishedChanges(true);
        }
    }, [location.state]);

    const refreshGridEvent = React.useCallback(async () => {
        await refresh();
        setUnpublishedChanges(false);
    }, [refresh]);

    const { status, lastEventReceived } = useSignalREvent(
        [PublishedEventType.EarlyBirdEventPublished],
        refreshGridEvent,
        divisionId
    );

    const visibleOffers = useMemo(
        () => [...offers].sort((a, b) => b.year - a.year),
        [offers]
    );

    const handleCreateOffer = () => {
        const year = getNextAvailableYear(visibleOffers);
        navigate(`/early-bird-offer/builder?year=${year}`);
    };

    const columns = [
        {
            title: "Pricing Year",
            dataIndex: "year",
            key: "year",
            render: (_: unknown, record: EarlyBirdOfferItemDto) => (
                <Typography.Link
                    onClick={() =>
                        navigate(`/early-bird-offer/builder?year=${record.year}`)
                    }
                >
                    {record.year}
                </Typography.Link>
            ),
        },
        {
            title: "Last Modified",
            dataIndex: "updateTimestamp",
            key: "updateTimestamp",
            render: (value: string) => formatDate(value),
        },
        {
            title: "Updated By",
            dataIndex: "lastModifiedBy",
            key: "lastModifiedBy",
            render: (value: string) => value || "—",
        },
    ];

    return (
        <div>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 24,
                    gap: 16,
                    flexWrap: "wrap",
                }}
            >
                <h1 style={{ margin: 0 }}>Early Bird Offer</h1>

                <Space>
                    <StatusIcon
                        status={status}
                        lastEventReceived={lastEventReceived}
                        unpublishedChanges={unpublishedChanges}
                        onManualRefresh={refresh}
                    />
                    <Button type="primary" onClick={handleCreateOffer}>
                        Create Offer
                    </Button>
                </Space>
            </div>

            <Table
                rowKey={(record) => record.year}
                columns={columns}
                dataSource={visibleOffers}
                pagination={false}
                locale={{
                    emptyText: (
                        <Empty
                            description="No Early Bird offers found"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        >
                            <Button type="primary" onClick={handleCreateOffer}>
                                Create Offer
                            </Button>
                        </Empty>
                    ),
                }}
            />
        </div>
    );
};

const mapStateToProps = (state: any): StateProps => ({
    offers: getOffers(state),
    divisionId: getDivisionId(state),
});

export const Page = connect(mapStateToProps)(PageComponent);